/* eslint-disable react-hooks/exhaustive-deps */

import React from 'react';
import { useSelector } from 'react-redux';
import { ButtonLink, Heading5, Text } from '@blueprint/ui';
import { SaveOutlineSmall, ArrowRightSmall } from '@blueprint/icons';
import { useSavedListings } from 'listing-details/hooks/useSavedListings/useSavedListings';
import { ListingImage } from 'shared/components/listing-data/listing-image';
import { ListingFeatureList } from 'shared/components/listing-data/listing-features';
import { LifecycleTag } from 'shared/components/listing-data/lifecycle-tag';
import {
    WidgetWrapper,
    HeadingRow,
    ScrollRow,
    CardWrapLink,
    StyledCard,
    CardDetails,
    Address,
    Price,
    StyledLinkWrapper,
    FlexColumn,
    StyledMiniCard,
    MiniCardDetails
} from './SavedPropertiesWidget.styles';
import { PublicationStatus } from 'graphql.generated';
import useIsWidthLessThanBreakpoint from 'shared/hooks/app/useIsWidthLessThanBreakpoint';
import { MOBILE_BREAKPOINT } from 'listing-details/constants';
import {
    trackClickSavedPropertyWidgetCard,
    trackClickSeeAllSavedProperties,
    trackSavedPropertiesWidgetLoad
} from './SavedPropertiesWidget.tracking';
import {
    withOptimizely,
    WithOptimizelyProps,
    selectForcedFeatures,
    getOptimizelyFeatureFlag
} from 'shared/optimizely';
import { withErrorBoundary } from 'shared/components/ErrorBoundary/ErrorBoundary';
import { useLoggedInStatus } from 'shared/hooks/useIsLoggedIn';
import { Saved_ListingFragment } from 'listing-details/queries/SavedListing.fragment.generated';

export const SAVED_PROPERTIES_WIDGET_OPTIMIZELY_FEATURE_NAME =
    'lex-323_saved_properties_widget';

export const LISTINGS_TO_DISPLAY_MOBILE = 3;
export const LISTINGS_TO_DISPLAY_DESKTOP = 4;

const getSmallAddress = (listing: Saved_ListingFragment) => {
    return listing.location?.streetName && listing.location?.postalCode
        ? `${listing.location?.streetName}, ${listing.location?.postalCode}`
        : listing.displayAddress;
};

type SavedPropertiesWidgetProps = {
    isMobile: boolean;
    savedListings: Saved_ListingFragment[];
};

export const SavedPropertiesWidget = ({
    isMobile,
    savedListings
}: SavedPropertiesWidgetProps) => {
    const savedPropertyCards = isMobile ? (
        <FlexColumn>
            {savedListings.map((listing) => (
                <CardWrapLink
                    key={listing.listingId}
                    data-testid="saved-property-card-mobile"
                    href={listing.listingUris?.detail || ''}
                    onClick={trackClickSavedPropertyWidgetCard}
                >
                    <StyledMiniCard>
                        <LifecycleTag listing={listing} />
                        <ListingImage image={listing.imagePreview} />
                        <MiniCardDetails
                            data-testid={`mini-saved-property-card-details-${listing.listingId}`}
                        >
                            <Address
                                color="text-placeholder"
                                isBold
                                size="secondary"
                            >
                                {getSmallAddress(listing)}
                            </Address>
                            <Price size="7" color="text-body" isBold>
                                {listing.pricing.label}
                            </Price>
                            <ListingFeatureList
                                features={listing.featurePreview}
                            />
                        </MiniCardDetails>
                    </StyledMiniCard>
                </CardWrapLink>
            ))}
        </FlexColumn>
    ) : (
        <ScrollRow>
            {savedListings.map((listing) => (
                <CardWrapLink
                    key={listing.listingId}
                    data-testid="saved-property-card-desktop"
                    href={listing.listingUris?.detail || ''}
                    onClick={trackClickSavedPropertyWidgetCard}
                >
                    <StyledCard>
                        <ListingImage image={listing.imagePreview} />
                        <CardDetails
                            data-testid={`saved-property-card-details-${listing.listingId}`}
                        >
                            <LifecycleTag listing={listing} />
                            <Text
                                size="secondary"
                                color="text-placeholder"
                                isBold
                            >
                                {listing.title}
                            </Text>
                            <Price size="7" color="text-body" isBold>
                                {listing.pricing.label}
                            </Price>
                            <Address size="8">{listing.displayAddress}</Address>
                            <ListingFeatureList
                                features={listing.featurePreview}
                            />
                        </CardDetails>
                    </StyledCard>
                </CardWrapLink>
            ))}
        </ScrollRow>
    );

    return (
        <WidgetWrapper
            data-testid="saved-property-widget"
            aria-labelledby="saved-properties-widget-header"
        >
            <HeadingRow>
                <SaveOutlineSmall />
                <Heading5 size="6" id="saved-properties-widget-header">
                    Recently saved properties
                </Heading5>
            </HeadingRow>
            {savedPropertyCards}
            <StyledLinkWrapper isCentred={isMobile}>
                <ButtonLink
                    variant="tertiary"
                    href="/favourites/properties/"
                    data-testid="see-all-saved-properties"
                    onClick={trackClickSeeAllSavedProperties}
                >
                    See all saved properties
                    <ArrowRightSmall />
                </ButtonLink>
            </StyledLinkWrapper>
        </WidgetWrapper>
    );
};

const SavedPropertiesWidgetContainer = ({
    optimizely
}: WithOptimizelyProps) => {
    const featureFlagOverrides = useSelector(selectForcedFeatures);

    const loggedInStatus = useLoggedInStatus();
    const loginStatusKnown = ['isLoggedIn', 'isNotLoggedIn'].includes(
        loggedInStatus
    );
    const isLoggedIn = loggedInStatus === 'isLoggedIn';

    const [isMobile] = useIsWidthLessThanBreakpoint([MOBILE_BREAKPOINT]);

    const listingsToDisplay = isMobile
        ? LISTINGS_TO_DISPLAY_MOBILE
        : LISTINGS_TO_DISPLAY_DESKTOP;

    const savedPropertiesWidgetIsEnabled = getOptimizelyFeatureFlag(
        SAVED_PROPERTIES_WIDGET_OPTIMIZELY_FEATURE_NAME,
        featureFlagOverrides,
        optimizely
    );
    const {
        savedListings: rawSavedListings,
        loading: loadingSavedListings
    } = useSavedListings(
        !savedPropertiesWidgetIsEnabled || !isLoggedIn,
        listingsToDisplay
    );

    const filteredSavedListings = rawSavedListings
        .filter(
            (listing) => listing.publicationStatus !== PublicationStatus.Expired
        )
        .slice(0, listingsToDisplay); // not really necessary but no harm either!

    const savedListings = React.useMemo(() => filteredSavedListings, [
        filteredSavedListings.length
    ]);

    React.useEffect(() => {
        if (loadingSavedListings || !loginStatusKnown) {
            return;
        }
        trackSavedPropertiesWidgetLoad(
            savedPropertiesWidgetIsEnabled,
            isLoggedIn,
            !!savedListings.length
        );
    }, [loadingSavedListings, loginStatusKnown]);

    if (
        !savedPropertiesWidgetIsEnabled ||
        !isLoggedIn ||
        savedListings.length === 0
    ) {
        return null;
    }

    return (
        <SavedPropertiesWidget
            savedListings={savedListings}
            isMobile={isMobile}
        />
    );
};

const widgetWithOptimizely = withOptimizely(SavedPropertiesWidgetContainer);

export default withErrorBoundary(widgetWithOptimizely, null);
